/* TODO(ASIMO-2105): cherrera2@ to remove google drive code once the experiment is over regardless of outcome */
import React, { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Grid, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	ANALYTICS_CHANNEL,
	buildAuthClickedEventPayload,
	buildAuthCompletedEventPayload,
	buildAuthFailedEventPayload,
	GoogleDriveAuthSource,
	withAnalyticsContext,
} from '../../services/analytics';
import { checkIfUserHasAuthorizedGoogleDriveConnector } from '../../services/connectedDataSource';
import { GOOGLE_DRIVE_OAUTH_SUCCESS_EVENT, triggerGoogleAuthFlow } from '../../services/googleAuth';
import { useBrowserStorage } from '../../services/hooks';
import {
	DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
	getGoogleDriveConnectionBannerStatus,
	getGoogleDriveConnectionStatus,
	GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY,
	GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
	type ThirdPartyConnectionStatus,
} from '../../services/storage';
import type { GoogleDriveBannerStatus } from '../../services/storage/thirdPartyConnectionsStatusStorage';

export const GOOGLE_DRIVE_CONNECT_BANNER_CLOSE_BUTTON_TEST_ID =
	'google-drive-connect-banner-close-button';

export const messages = defineMessages({
	googleDriveConnectBannerLink: {
		id: 'fabric.linkPickerAtlassianPlugin.googleDriveConnectBannerLink',
		defaultMessage: 'Connect your apps to see more links',
		description: 'Link message shown in the google drive connect banner',
	},
	googleDriveConnectBannerClose: {
		id: 'fabric.linkPickerAtlassianPlugin.googleDriveConnectBannerClose',
		defaultMessage: 'Dismiss',
		description: 'Close button message shown in the google drive connect banner',
	},
});

const styles = cssMap({
	containerBaseStyles: {
		borderRadius: '4px',
		backgroundColor: token('color.background.neutral', '#F5F5F5'),
		gridTemplateColumns: 'min-content auto 1fr auto',
		marginTop: token('space.100', '8px'),
		paddingTop: token('space.150', '12px'),
		paddingRight: token('space.150', '12px'),
		paddingBottom: token('space.150', '12px'),
		paddingLeft: token('space.150', '12px'),
	},
	pressableInlineStyles: {
		paddingLeft: token('space.100', '8px'),
	},
	pressableStyles: {
		color: token('color.link', '#0052CC'),
		font: token('font.body.small', '14px'),
		fontWeight: token('font.weight.medium', '500'),
		padding: '0',
	},
	closeButtonContainer: {
		justifySelf: 'end',
	},
});

const GoogleDriveIcon = () => {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="#FFFFFF"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect
				x="0.254162"
				y="0.254162"
				width="15.4917"
				height="15.4917"
				rx="3.74584"
				stroke="#0B1228"
				strokeOpacity="0.14"
				strokeWidth="0.508324"
			/>
			<g clipPath="url(#clip0_303_5302)">
				<path
					d="M3.4728 11.3855L3.9432 12.1939C4.04095 12.3641 4.18146 12.4978 4.34641 12.595L6.02644 9.70197H2.66638C2.66638 9.89038 2.71526 10.0788 2.813 10.249L3.4728 11.3855Z"
					fill="#0066DA"
				/>
				<path
					d="M7.99972 6.29837L6.31968 3.4053C6.15474 3.50255 6.01422 3.63626 5.91648 3.80644L2.813 9.15497C2.71705 9.32148 2.66651 9.51005 2.66638 9.70198H6.02644L7.99972 6.29837Z"
					fill="#00AC47"
				/>
				<path
					d="M11.6534 12.595C11.8183 12.4978 11.9588 12.3641 12.0566 12.1939L12.2521 11.8596L13.1868 10.249C13.2845 10.0788 13.3334 9.89038 13.3334 9.70197H9.97308L10.6881 11.0999L11.6534 12.595Z"
					fill="#EA4335"
				/>
				<path
					d="M7.99998 6.29846L9.68001 3.40539C9.51506 3.30814 9.32567 3.25952 9.13018 3.25952H6.86977C6.67428 3.25952 6.48489 3.31422 6.31995 3.40539L7.99998 6.29846Z"
					fill="#00832D"
				/>
				<path
					d="M9.97271 9.70197H6.02616L4.34613 12.595C4.51108 12.6923 4.70046 12.7409 4.89596 12.7409H11.1029C11.2984 12.7409 11.4878 12.6862 11.6527 12.595L9.97271 9.70197Z"
					fill="#2684FC"
				/>
				<path
					d="M11.6352 6.48071L10.0834 3.80644C9.98567 3.63626 9.84516 3.50255 9.68021 3.4053L8.00018 6.29837L9.97346 9.70198H13.3274C13.3274 9.51357 13.2785 9.32515 13.1808 9.15497L11.6352 6.48071Z"
					fill="#FFBA00"
				/>
			</g>
			<defs>
				<clipPath id="clip0_303_5302">
					<rect
						width="10.6667"
						height="9.48148"
						fill="white"
						transform="translate(2.6662 3.2594)"
					/>
				</clipPath>
			</defs>
		</svg>
	);
};

interface GoogleDriveConnectBannerProps {
	cloudId: string;
}

const GoogleDriveConnectBanner: React.FC<GoogleDriveConnectBannerProps> = ({ cloudId }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [integrationDetails, setIntegrationDetails] =
		useBrowserStorage<ThirdPartyConnectionStatus | null>(
			GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
			getGoogleDriveConnectionStatus(),
		);
	const [bannerDismissState, setBannerDismissState] =
		useBrowserStorage<GoogleDriveBannerStatus | null>(
			GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY,
			getGoogleDriveConnectionBannerStatus(),
		);

	const hasBannerBeenDismissed = bannerDismissState?.hasBannerBeenDismissed || false;
	const hasAuthorized = integrationDetails?.isUserOAuthed || false;
	const hasIntegrationDetails = Object.keys(integrationDetails || {}).length > 0;
	const hideBanner = hasAuthorized || hasBannerBeenDismissed || !hasIntegrationDetails;

	const trackEvent = (event: AnalyticsEventPayload) => {
		createAnalyticsEvent(event).fire(ANALYTICS_CHANNEL);
	};

	const handleBroadcastedOAuthSuccess = (_: Event) => {
		setBannerDismissState({ hasBannerBeenDismissed: true });
	};

	// If the user has authorized through the empty state UI, close the banner.
	useEffect(() => {
		window.addEventListener(GOOGLE_DRIVE_OAUTH_SUCCESS_EVENT, handleBroadcastedOAuthSuccess);
		return () => {
			window.removeEventListener(GOOGLE_DRIVE_OAUTH_SUCCESS_EVENT, handleBroadcastedOAuthSuccess);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleConnectionClick = () => {
		if (!integrationDetails || !integrationDetails.outboundAuthUrl) {
			return;
		}

		trackEvent(buildAuthClickedEventPayload(GoogleDriveAuthSource.CONNECT_BANNER));

		triggerGoogleAuthFlow(integrationDetails.outboundAuthUrl)
			.then(() => {
				setIntegrationDetails({
					...integrationDetails,
					isUserOAuthed: true,
					expirationDate: Date.now() + DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
				});
				trackEvent(buildAuthCompletedEventPayload(GoogleDriveAuthSource.CONNECT_BANNER));
			})
			.catch((_) => {
				trackEvent(buildAuthFailedEventPayload(GoogleDriveAuthSource.CONNECT_BANNER));
			});
	};

	// Fetch connection status if it hasn't been fetched yet and the banner hasn't been dismissed
	useEffect(() => {
		const fetchGoogleDriveConnectionStatus = async () => {
			await checkIfUserHasAuthorizedGoogleDriveConnector(cloudId);
			setIntegrationDetails(getGoogleDriveConnectionStatus());
		};
		if (!integrationDetails && !hasBannerBeenDismissed) {
			fetchGoogleDriveConnectionStatus();
		}
	}, [cloudId, integrationDetails, hasBannerBeenDismissed, setIntegrationDetails]);

	// If the user has authorized or the banner has been dismissed, don't render the banner
	if (hideBanner) {
		return null;
	}

	return (
		<Grid alignItems="center" xcss={cx(styles.containerBaseStyles)}>
			<GoogleDriveIcon />
			<Inline alignBlock="center" xcss={styles.pressableInlineStyles}>
				<Pressable xcss={styles.pressableStyles} onClick={handleConnectionClick}>
					{formatMessage(messages.googleDriveConnectBannerLink)}
				</Pressable>
			</Inline>
			<Inline alignBlock="center" xcss={styles.closeButtonContainer}>
				<IconButton
					appearance="subtle"
					label={formatMessage(messages.googleDriveConnectBannerClose)}
					spacing="compact"
					icon={() => <CrossIcon label="Close" size="medium" />}
					onClick={() => setBannerDismissState({ hasBannerBeenDismissed: true })}
					testId={GOOGLE_DRIVE_CONNECT_BANNER_CLOSE_BUTTON_TEST_ID}
				/>
			</Inline>
		</Grid>
	);
};

const GoogleDriveConnectBannerWithAnalytics = withAnalyticsContext(GoogleDriveConnectBanner);

export const renderGoogleDriveConnectBanner = (cloudId: string | null) => {
	if (!cloudId) {
		return null;
	}
	return <GoogleDriveConnectBannerWithAnalytics cloudId={cloudId} />;
};
